// 第一層基礎元件之間共用的 Tailwind class 常數。
//
// 為什麼要有這個檔：§8.1 規定「相同視覺值出現兩次以上時必須先評估建立具名 token，
// 不得在多個頁面重複散落 arbitrary values」。下面這幾個值來自原系統 CSS，但 theme.css
// 目前沒有對應 token，而 theme.css 是契約檔、本次不得修改，所以集中放這一個檔案，
// 讓同一個字面字串只出現一次（Tailwind 也只需要掃到這一份）。
//
// 【證據等級】§7 第 3 級（原始碼）。每個值都註明來源檔與行號，但那是「CSS 檔裡寫什麼」，
// 不是瀏覽器算出來的 computed style ── 一律【雛形值待實機校準】。
// 來源根目錄：ePortfolio_work/ePortfolio-tsgh/WebContent/css/

/**
 * 表單欄位（input／textarea／select）的共用外觀。
 *
 * 對應原系統 `.form-control`（css/style.css:1813-1825 覆寫 bootstrap.css:2551-2568）：
 *   display:block / width:100% / padding:6px 12px / border-radius:1px / font-size:16px
 *   border:1px solid #e5e6e7  → 雛形改用 token --color-border (#e7eaec)，差一點點，
 *                               不另外開 arbitrary value；【雛形值待實機校準】
 *   focus border-color:#637d4c（css/style.css:1826-1829）→ focus:border-brand
 *   placeholder color:#999（bootstrap.css:2580）→ token --color-ink-muted (#999999)
 *
 * 刻意不含「邊框顏色」與「disabled 底色」：這兩項各元件不同（invalid／select 專屬底色），
 * 混在同一個字串裡會變成兩條同權重的 Tailwind 規則互相蓋，勝負取決於 CSS 產生順序而非
 * class 的書寫順序，是很難查的 bug。改由呼叫端各自附加。
 */
export const FIELD_BASE_CLASS =
  'block w-full rounded-[1px] border bg-surface px-3 py-[6px] text-[16px] text-ink ' +
  'placeholder:text-ink-muted transition-colors focus:border-brand disabled:cursor-not-allowed';

/**
 * 欄位邊框顏色對照表（靜態，§8.1 不得用樣板字串動態組）。
 * invalid 對應 `.has-error .form-control { border-color:#ed5565 }`（css/style.css:1836-1838）。
 */
export const FIELD_BORDER_CLASS = {
  normal: 'border-border',
  invalid: 'border-danger',
} as const;

/** 依 invalid 狀態取邊框 class。 */
export function fieldBorderClass(invalid: boolean): string {
  return invalid ? FIELD_BORDER_CLASS.invalid : FIELD_BORDER_CLASS.normal;
}

/**
 * 連結文字外觀。
 * 原系統 `a { color:#3399ff }`（style-ePortfolio.css:1-3，覆寫 bootstrap 的 #337ab7），
 * hover 底線來自 `a:hover { text-decoration:underline }`（bootstrap.css:1103-1107）。
 * Link 與 Button 的 link variant 共用同一個值，所以放這裡而不是各寫一份。
 * theme.css 沒有 link 色 token，這是本檔存在的主要原因之一。【雛形值待實機校準】
 */
export const LINK_TEXT_CLASS = 'text-[#3399ff] hover:underline';
